import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { toast } from "@shared/ui/use-toast";
import { downloadFileByUrl, viewFileByUrl } from "@shared/utils/utils";
import { Download, Eye, File, FileText, Image, Upload } from "lucide-react";
import React, { useEffect, useState } from "react";

import {
  useAddDocuments,
  useNetworkDetails,
} from "@features/ax/assetRegistry/networks/hooks";
import FileUploadDialog from "@features/ax/shared/DocumentUploadModal";
import { logEvent } from "@shared/analytics/analytics";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { PageHeader } from "@shared/components/PageHeader";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useParams } from "react-router-dom";

const FileCard: React.FC<{ file: any }> = ({ file }) => {
  const handleDownload = () => {
    downloadFileByUrl(file.doc);
  };

  const handleView = () => {
    viewFileByUrl(file.doc);
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <HoverRevealText
              text={file.fileName}
              maxLength={20}
            ></HoverRevealText>
            <div className="space-y-1 text-sm text-gray-600">
              <p>Date: {file.createdDate}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              className="h-8 w-8 p-0"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleView}
              className="h-8 w-8 p-0"
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const NetworkFilesTab = () => {
  const { id } = useParams<{ id: string }>();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: filesDataData } = useNetworkDetails({
    tab: "documents",
    networkId: id,
    remote_utility_id: remoteUtilityId,
  });
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  useEffect(() => {
    logEvent("files_tab_viewed");
  }, []);
  const documents = filesDataData?.result || [];
  const addDocumentsMutation = useAddDocuments();

  const handleFilesUpload = async (files: File[]) => {
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("doc", file);
      });
      formData.append("remote_utility_id", remoteUtilityId);

      await addDocumentsMutation.mutateAsync({
        networkId: id || "",
        payload: formData,
      });
    } catch (err: any) {
      toast({
        title: "Upload failed",
        description: err?.message || "Something went wrong while uploading",
        variant: "destructive",
      });
    }
  };
  // segregate files into media and documents
  const mediaExtensions = ["jpg", "jpeg", "png", "mp4"];
  const documentExtensions = ["pdf", "doc", "docx", "xls", "xlsx"];

  const mediaFiles = documents?.filter((f) =>
    mediaExtensions.includes(f.doc?.split(".").pop()?.toLowerCase())
  );

  const documentFiles = documents?.filter((f) =>
    documentExtensions.includes(f.doc?.split(".").pop()?.toLowerCase())
  );

  const otherFiles = documents?.filter(
    (f) =>
      !documentExtensions.includes(f.doc?.split(".").pop()?.toLowerCase()) &&
      !mediaExtensions.includes(f.doc?.split(".").pop()?.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <FileText className="h-5 w-5 text-indigo-600" />
          </div>
          <PageHeader
            title="Files & Documents"
            subtitle={`Manage facility-related documents, media files, and attachments (${documents.length} files)`}
          />
        </div>
        <Button
          className="flex items-center gap-2"
          onClick={() => setIsUploadOpen(true)}
          isLoading={addDocumentsMutation.isPending}
        >
          <Upload className="h-4 w-4" />
          Upload File
        </Button>
      </div>

      <FileUploadDialog
        isOpen={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        onFilesUpload={handleFilesUpload}
        title="Upload Network Documents"
        description="Attach technical drawings, manuals, and other files related to this network"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.xls,.xlsx"
      />

      {/* Media Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Media Files ({mediaFiles?.length})
        </h3>
        {mediaFiles?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mediaFiles?.map((file) => (
              <FileCard key={file.id} file={file} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Image className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No media files uploaded yet</p>
          </div>
        )}
      </div>

      {/* Documents Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Documents ({documentFiles?.length})
        </h3>
        {documentFiles?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documentFiles?.map((file) => (
              <FileCard key={file.id} file={file} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <File className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No documents uploaded yet</p>
          </div>
        )}
      </div>

      {otherFiles?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Others ({otherFiles.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherFiles.map((file) => (
              <FileCard key={file.id} file={file} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
